import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import { FormDataType } from '@tn/formbuilder/utils/schema-types';
import {
    cacheKey as FEATURE_FLAGS_CACHE_KEY,
    FEATURE_FLAG_NAMES,
} from '@tn/foundation/entities/feature-flags/constants';
import { getFeatureFlag } from '@tn/foundation/entities/feature-flags/utils/getFeatureFlag';
import { EmailCheckpointResult } from '@tn/foundation/graphql/__generated/gql/graphql';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import throwGraphQLError from '@tn/foundation/utils/throwGraphQLError';
import request, { RequestDocument } from 'graphql-request';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    fromPromise,
    Mapper,
    OutputFrom,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { cleanFormData } from '../../utils/cleanFormData/cleanFormData';
import { getErrorMessageFromEvent, getEvaluatedRoute } from '../helpers';

import { checkEmailQuery, emailCheckpointMutation } from './graphql/constants';
import { trackEmailCheckpointNextSelect } from './tracking';

export type CheckEmailFlowContext = {
    email: string;
    formBuilderRef: FormBuilderRef | null;
    destinationState?: 'signIn' | 'signUp';
    code?: string;
    url: string;
};

export type CheckEmailFlowOutput = Pick<CheckEmailFlowContext, 'email' | 'destinationState'>;
const CHECKPOINT_RESULT_TO_DESTINATION: Record<EmailCheckpointResult, 'signIn' | 'signUp'> = {
    // TODO: map to a dedicated activation flow when available
    NOT_FOUND: 'signUp',
    IDENTITY_LINKED_TO_TENANT: 'signIn',
    IDENTITY_NEEDS_ACTIVATION: 'signUp',
};

const isEmailCheckpointResult = (value: unknown): value is EmailCheckpointResult =>
    typeof value === 'string' && value in CHECKPOINT_RESULT_TO_DESTINATION;

export const submitFormCheckEmailActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise(async ({ input }) => {
            const cleanedFormData = cleanFormData(input as FormDataType);
            const graphqlEndpoint = `/api/tn/signup/graphql/`;
            const email = cleanedFormData.email as string;

            const featureFlagsData = queryClient.getQueryData<{
                featureFlags?: Record<string, unknown>;
            }>([FEATURE_FLAGS_CACHE_KEY]);
            const featureFlags = featureFlagsData?.featureFlags || {};
            const isUipEnabled = getFeatureFlag(featureFlags)(
                FEATURE_FLAG_NAMES.UNIFIED_IDENTITY_PLATFORM,
                false,
            ) as boolean;

            if (isUipEnabled) {
                const response = (await request(
                    graphqlEndpoint,
                    emailCheckpointMutation as RequestDocument,
                    { email },
                )) as { emailCheckpoint: { result: EmailCheckpointResult } };

                const result = response.emailCheckpoint?.result;
                if (!isEmailCheckpointResult(result)) {
                    appLogger.error('Unexpected email checkpoint result', {
                        component: 'submitFormCheckEmailActor',
                        endpoint: graphqlEndpoint,
                        errorCode: 'UNKNOWN_EMAIL_CHECKPOINT_RESULT',
                        hasResult: Boolean(result),
                    });
                    throwGraphQLError('Unable to verify email status.');
                }

                return { destinationState: CHECKPOINT_RESULT_TO_DESTINATION[result] };
            }

            const response = (await request(graphqlEndpoint, checkEmailQuery as RequestDocument, {
                formFields: { email },
            })) as { checkEmail: { success: boolean } };

            return { destinationState: response.checkEmail.success ? 'signIn' : 'signUp' };
        }),

    inputActor: <
        Mapper<
            CheckEmailFlowContext,
            AnyEventObject,
            Omit<CheckEmailFlowContext, 'formBuilderRef'>,
            AnyEventObject
        >
    >(({ context: { formBuilderRef: _formBuilderRef, ...rest } }) => {
        return { ...rest };
    }),
};

export const checkEmailFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router } = rawParams as InitMachineParams;
        const machine = setup({
            types: {
                context: {} as CheckEmailFlowContext,
                events: {} as
                    | {
                          type: 'xstate.init';
                          input: { code?: string; url: string };
                      }
                    | {
                          type: 'SUBMIT_CLICK';
                          params: CheckEmailFlowContext;
                      }
                    | {
                          type: 'CONNECT_FORMBUILDER_REF';
                          params: FormBuilderRef;
                      }
                    | { type: 'SUBMITTING' }
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof submitFormCheckEmailActor.initActor>>,
                          'submitting'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof submitFormCheckEmailActor.initActor>>,
                          'submitting'
                      >,
                input: {},
            },
            guards: {},
            actions: {
                goToCheckEmailRoute: enqueueActions(({ enqueue: _enqueue, context, event }) => {
                    assertEvent(event, 'xstate.init');
                    const url = context.url || '/account/check-email';
                    const evaluatedUrl = getEvaluatedRoute(router.pathname, url);

                    if (evaluatedUrl) {
                        router.push(evaluatedUrl);
                    }
                }),
                submitForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SUBMIT_CLICK');
                    context.formBuilderRef?.toggleLoading(true);
                    trackEmailCheckpointNextSelect();
                    enqueue.assign({ ...event.params });
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                saveDataFromSuccessfulCheckEmail: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    enqueue.assign({
                        destinationState: event.output.destinationState ?? 'signUp',
                    });
                    context.formBuilderRef?.toggleLoading(false);
                }),
                simulateResultSignIn: enqueueActions(({ enqueue, context }) => {
                    enqueue.assign({ destinationState: 'signIn' });
                    context.formBuilderRef?.toggleLoading(false);
                }),
                simulateResultSignUp: enqueueActions(({ enqueue, context }) => {
                    enqueue.assign({ destinationState: 'signUp' });
                    context.formBuilderRef?.toggleLoading(false);
                }),
                assignFormBuilderRef: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    enqueue.assign({ formBuilderRef: event.params });
                }),
                handleError: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleError(event.error.message);
                },
                handleErrorWithBanner: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');

                    const errorMessage = getErrorMessageFromEvent(event);
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleBanner({
                        message: errorMessage,
                        variant: 'critical',
                    });
                },
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                email: '',
                formBuilderRef: null,
                url: '',
                ...input,
            }),
        });

        return machine;
    },
    outputActor: <
        Mapper<CheckEmailFlowContext, AnyEventObject, CheckEmailFlowOutput, AnyEventObject>
    >(({ context }) => {
        return { ...context };
    }),
};
